import React, { useState, useEffect, useRef } from "react";
import { Modal, TextInput, InlineNotification } from "@carbon/react";
import "./createSubagentModal.scss";
import { useTranslation } from "react-i18next";
import validator from "validator";
import { useCreateSubagent } from "../../hooks/useCreateSubagent";
import { useAddChat } from "../../hooks/useAddChat";
import { queryClient } from "../..";

export default function CreateSubagentModal({
  openModal,
  setOpenModal,
  onSuccess,
}) {
  const { t } = useTranslation();
  const formRef = useRef(null);

  const [validationEmail, setValidationEmail] = useState({
    invalid: false,
    invalidText: "",
  });
  const [validationPublicName, setValidationPublicName] = useState({
    invalid: false,
    invalidText: "",
  });
  const [errorNotification, setErrorNotification] = useState({
    visibility: false,
    title: "",
    subtitle: "",
    kind: "",
  });

  const [email, setEmail] = useState("");
  const [publicName, setPublicName] = useState("");
  const [chatName, setChatName] = useState("");

  const mutationSubagent = useCreateSubagent();
  const mutationAddChat = useAddChat();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validationEmail.invalid || validationPublicName.invalid) return;

    try {
      const subagentResponse = await mutationSubagent.mutateAsync({
        email,
        publicname: publicName,
      });

      const guid = subagentResponse?.data?.guid;
      if (!guid) throw new Error("No guid received");

      await mutationAddChat.mutateAsync({ guid, name: chatName });

      setOpenModal(false);
      setEmail("");
      setPublicName("");
      setChatName("");
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["getInfo"] });
    } catch {
      setErrorNotification({
        visibility: true,
        title: "authentication.notification.requestError500.title",
        subtitle: "authentication.notification.requestError500.serverError",
        kind: "error",
      });
    }
  };

  useEffect(() => {
    if (mutationSubagent.isError) {
      setErrorNotification({
        visibility: true,
        title: "authentication.notification.requestError500.title",
        subtitle: "authentication.notification.requestError500.serverError",
        kind: "error",
      });
    }
  }, [mutationSubagent.isError, setErrorNotification]);

  useEffect(() => {
    if (email && !validator.isEmail(email)) {
      setValidationEmail({
        invalid: true,
        invalidText: "authentication.validationErrors.emailValidation",
      });
    } else {
      setValidationEmail({ invalid: false, invalidText: "" });
    }
  }, [email, setValidationEmail]);

  useEffect(() => {
    if (
      publicName &&
      (!validator.isLength(publicName, { min: 5 }) ||
        validator.isNumeric(publicName) ||
        !validator.matches(
          publicName,
          /^[a-zA-Z0-9_,.\-'\[\]{}()!?@#$%^&*~`+=/|\\:;*"]+$/
        ))
    ) {
      setValidationPublicName({
        invalid: true,
        invalidText: "authentication.validationErrors.publicNameValidation",
      });
    } else {
      setValidationPublicName({ invalid: false, invalidText: "" });
    }
  }, [publicName, setValidationPublicName]);

  return (
    <Modal
      open={!!openModal}
      onRequestClose={() => setOpenModal(false)}
      modalHeading={`${t("main.admin.create_subagent.title")}`}
      modalLabel={`${t("main.admin.create_subagent.label")}`}
      primaryButtonText={`${t("main.admin.create_subagent.modal.send")}`}
      secondaryButtonText={`${t("main.chat.addModal.cancel")}`}
      onRequestSubmit={() => {
        formRef.current.requestSubmit();
      }}
      onSecondarySubmit={() => setOpenModal(false)}
      closeButtonLabel={`${t("main.modalText.close")}`}
      className="create_subagent_modal"
    >
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="create_subagent_modal_main"
      >
        <TextInput
          id="text-input-1"
          labelText={`${t("main.admin.create_subagent.modal.email")}`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          maxCount={64}
          enableCounter
          invalid={validationEmail.invalid}
          invalidText={
            validationEmail.invalidText ? t(validationEmail.invalidText) : ""
          }
        />
        <TextInput
          id="text-input-3"
          labelText={`${t("main.admin.create_subagent.modal.public_name")}`}
          value={publicName}
          onChange={(e) => setPublicName(e.target.value)}
          required
          maxCount={64}
          enableCounter
          invalid={validationPublicName.invalid}
          invalidText={
            validationPublicName.invalidText
              ? t(validationPublicName.invalidText)
              : ""
          }
        />
        <TextInput
          id="text-input-4"
          labelText={`${t("main.admin.create_subagent.modal.chat_name")}`}
          value={chatName}
          onChange={(e) => setChatName(e.target.value)}
          required
        />
      </form>
      {errorNotification.visibility && (
        <InlineNotification
          title={t(errorNotification.title)}
          subtitle={t(errorNotification.subtitle)}
          kind={errorNotification.kind}
          onClose={() =>
            setErrorNotification({
              visibility: false,
              title: "",
              subtitle: "",
              kind: "",
            })
          }
          lowContrast={true}
          aria-label={t("authentication.notification.closeNotification")}
          className="notification"
        />
      )}
    </Modal>
  );
}
